import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useExtractServerError } from 'hooks';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/index';

import ClosedTicketsUi from './ClosedTickets.ui';
import useClosedTicketsQuery from './useClosedTicketsQuery';
import { fromIsoDate, toIsoDate } from '../DirectClaims/format';
import { ClosedTicketsParams } from './ClosedTickets.types';

const DEFAULT_ROWS = 50;

function pageNumber(value: string | null, fallback: number) {
  const number = Number(value);
  return value !== null && Number.isInteger(number) && number >= 0
    ? number
    : fallback;
}

function readParams(searchParams: URLSearchParams): ClosedTicketsParams {
  const rows = pageNumber(searchParams.get('rows'), DEFAULT_ROWS);

  return {
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || '',
    category: searchParams.get('category') || 'ALL',
    reference: searchParams.get('reference') || '',
    first: pageNumber(searchParams.get('first'), 0),
    rows: ROWS_PER_PAGE_OPTIONS.includes(rows) ? rows : DEFAULT_ROWS,
  };
}

// Empty filters and the default category stay out of the URL, so a plain link
// is the full list.
function toSearchParams(params: ClosedTicketsParams) {
  const entries = Object.entries(params)
    .filter(([key, value]) => value !== '' && !(key === 'category' && value === 'ALL'))
    .map(([key, value]) => [key, String(value)]);

  return Object.fromEntries(entries);
}

export default function ClosedTickets() {
  //* Hooks Init
  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * The URL is the query: filters and page, so a shared link opens the same
   * page. With nothing in it the list starts at the latest service date.
   */
  const params = useMemo(() => readParams(searchParams), [searchParams]);

  //* Local State
  const [reference, setReference] = useState(params.reference);

  const ticketsQuery = useClosedTicketsQuery(params);
  const { extractErrorMessage } = useExtractServerError();

  const tickets = ticketsQuery.data?.data.data ?? [];
  const totalRecords = ticketsQuery.data?.data.total ?? 0;
  const lastSyncedAt = ticketsQuery.data?.data.lastSyncedAt ?? null;

  //* Handlers
  // Any new filter starts again from the first page.
  function update(next: Partial<ClosedTicketsParams>) {
    setSearchParams(toSearchParams({ ...params, first: 0, ...next }));
  }

  function handleSearchChange(_name: string, value: string) {
    setReference(value);
  }

  function handleSubmitSearch() {
    update({ reference: reference.trim() });
  }

  function handleStartDateChange(event: any) {
    update({ startDate: event.value ? toIsoDate(event.value) : '' });
  }

  function handleEndDateChange(event: any) {
    update({ endDate: event.value ? toIsoDate(event.value) : '' });
  }

  function handleCategoryChange(value: string) {
    update({ category: value });
  }

  function handlePageChange(event: { first: number; rows: number }) {
    setSearchParams(
      toSearchParams({ ...params, first: event.first, rows: event.rows })
    );
  }

  return (
    <ClosedTicketsUi
      tickets={tickets}
      totalRecords={totalRecords}
      lastSyncedAt={lastSyncedAt}
      loading={ticketsQuery.isFetching}
      errorMessage={
        ticketsQuery.isError ? extractErrorMessage(ticketsQuery.error) : ''
      }
      reference={reference}
      params={params}
      startDate={fromIsoDate(params.startDate)}
      endDate={fromIsoDate(params.endDate)}
      handlers={{
        handleSearchChange,
        handleSubmitSearch,
        handleStartDateChange,
        handleEndDateChange,
        handleCategoryChange,
        handlePageChange,
      }}
    />
  );
}
